'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { useQuery } from '@tanstack/react-query'
import Button from '@ui/ButtonNew/Button'
import { axiosProxy } from '@utils/axios'
import { getMailruAuthUrl, getTelegramAuthUrl } from '@view/auth/api'
import { setCookie } from 'cookies-next'
import { observer } from 'mobx-react-lite'

import { writeBrowserAuthMethodMarker } from '@/shared/analytics/auth-method-marker'
import MailruIcon from '../../../assets/social/mailru.svg'
import TelegramIcon from '../../../assets/social/telegram.svg'
import styles from './styles.module.css'

const ICON_SIZE = 24

const checkTelegramAuth = async (token: string) => {
  if (!token) {
    return
  }
  const { data: response } = await axiosProxy.get('/auth/telegram/check/' + token)
  return response.data
}

interface SocialButtonsProps {
  state: string
  redirectPage: string
}

export const SocialButtons = observer(({ state, redirectPage }: SocialButtonsProps) => {
  const [telegramToken, setTelegramToken] = useState('')
  const [telegramUrl, setTelegramUrl] = useState('')
  const [telegramQr, setTelegramQr] = useState('')

  const redirectToOauthUrl = async (getUrl: () => Promise<{ data: { data?: { url?: string } } }>) => {
    try {
      setCookie('auth-callback', redirectPage, { path: '/', maxAge: 60 * 60 })
      const { data: response } = await getUrl()
      if (response.data?.url) {
        window.location.href = response.data.url
      }
    } catch {
      return
    }
  }

  const startTelegram = async () => {
    try {
      setCookie('auth-callback', redirectPage, { path: '/', maxAge: 60 * 60 })
      const { data: response } = await getTelegramAuthUrl()
      if (response.data?.token) {
        setTelegramUrl(response.data.url)
        setTelegramQr(response.data.qr)
        setTelegramToken(response.data.token)
      }
    } catch {
      return
    }
  }

  const { data: telegramStatus } = useQuery({
    queryKey: ['telegram_auth_check', telegramToken],
    enabled: !!telegramToken,
    queryFn: () => checkTelegramAuth(telegramToken),
    refetchOnWindowFocus: true,
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  })

  // redirectPage уже учитывает, пришёл ли пользователь из плагина, и куда его вернуть после входа.
  useEffect(() => {
    if (!telegramStatus?.confirmed) {
      return
    }
    writeBrowserAuthMethodMarker('telegram')
    window.location.href = redirectPage
  }, [telegramStatus?.confirmed, redirectPage])

  if (telegramToken) {
    return <TelegramQr qr={telegramQr} url={telegramUrl} />
  }

  return (
    <div className={`${styles.container} grid grid-cols-2 gap-4`}>
      <SocialButton
        icon={MailruIcon}
        label="Mail.RU"
        alt="Войти через Mail.RU"
        onClick={() => redirectToOauthUrl(() => getMailruAuthUrl(state))}
      />
      <SocialButton
        icon={TelegramIcon}
        label="Telegram"
        alt="Войти через Telegram"
        disabled={!!state}
        onClick={startTelegram}
      />
    </div>
  )
})

interface SocialButtonProps {
  icon: string
  label: string
  alt: string
  disabled?: boolean
  onClick: () => void
}

const SocialButton = ({ icon, label, alt, disabled, onClick }: SocialButtonProps) => {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 py-4 transition-colors hover:border-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Image priority src={icon} width={ICON_SIZE} height={ICON_SIZE} alt={alt} />
      <span className="text-sm text-gray-700">{label}</span>
    </button>
  )
}

const TelegramQr = ({ qr, url }: { qr: string; url: string }) => {
  const [cacheBuster] = useState(() => Date.now())

  return (
    <div className="mt-2 mb-4 flex items-start gap-4 text-left">
      <img
        src={`${qr}?nc=${cacheBuster}`}
        alt="Привязка Telegram по QR-коду"
        width={150}
        height={150}
        className="w-[150px]"
      />
      <div className="flex-1 text-sm text-gray-500">
        Перейдите в диалог с чат-ботом <b>@EvirmaBot</b> с помощью QR-кода или кнопки ниже. В окне чата{' '}
        <b>нажмите&nbsp;Start</b> и следуйте инструкциям.
        {url && (
          <Button view="success" target="_blank" href={url} className="mt-4 text-sm">
            Перейти в чат-бот
          </Button>
        )}
      </div>
    </div>
  )
}
